<template>
  <ProductCardCarouselSkeleton
    v-if="
      recommendationsStatus === 'idle' || recommendationsStatus === 'pending'
    "
    :with-title="!!title"
  />
  <section
    v-else-if="recommendations.length"
    data-qa="recommendation-carousel"
    data-test="recommendation-carousel"
  >
    <ProductCardCarousel
      :products="recommendations"
      :subtitle
      :title
      :tracking="{ ...trackingData, provider: 'algolia' }"
      v-on="withAddToCart ? { addToCart: addToCartHandler } : {}"
    />
  </section>
</template>

<script lang="ts" setup>
import { watch } from 'vue'

import { useAlgoliaRecommendations } from '../composables/useAlgoliaRecommendations/useAlgoliaRecommendations'
import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectIds,
  AlgoliaRecommendationsOptions,
} from '../composables/useAlgoliaRecommendations/useAlgoliaRecommendations.types'
import type { ExtraTrackingData } from '../composables/useProductTracking'

import ProductCardCarousel from './ProductCardCarousel/ProductCardCarousel.vue'
import ProductCardCarouselSkeleton from './ProductCardCarousel/ProductCardCarouselSkeleton.vue'

export type RecommendationAlgoliaCarouselProps = {
  recommendationQuery: {
    objectIds: AlgoliaRecommendationsObjectIds
    model: AlgoliaRecommendationsModel
    options?: AlgoliaRecommendationsOptions
  }
  title?: string
  subtitle?: string
  withAddToCart?: boolean
  trackingData?: ExtraTrackingData
  clientOnly?: boolean
}

const {
  title,
  subtitle,
  recommendationQuery,
  withAddToCart = false,
  trackingData = {},
  clientOnly,
} = defineProps<RecommendationAlgoliaCarouselProps>()

const emit = defineEmits(['error', 'loaded', 'refresh', 'added-to-cart'])
const {
  data: recommendations,
  error: recommendationsError,
  status: recommendationsStatus,
} = useAlgoliaRecommendations(
  () => recommendationQuery.objectIds,
  recommendationQuery.model,
  () => recommendationQuery.options,
  { server: !clientOnly },
)

function addToCartHandler({
  status,
  product,
}: {
  status: 'success' | 'error'
  product: { listingId: string }
}) {
  if (status === 'success') {
    emit('refresh', product.listingId)
    emit('added-to-cart', product)
  }
}

watch(
  recommendationsStatus,
  () => {
    if (recommendationsStatus.value === 'success') {
      if (!recommendations.value.length) {
        emit('error', null)

        return
      }
      emit('loaded', recommendations.value)
    } else if (recommendationsStatus.value === 'error') {
      emit('error', recommendationsError.value)
    }
  },
  { immediate: true },
)
</script>
